import { motion } from 'framer-motion';
import type { Card as CardType, Rank, Suit as SuitType } from '@durakapp/engine';
import { Suit } from './Suit.js';
import { suitColor } from './suitColor.js';

const RANK_LABEL: Record<Rank, string> = {
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  11: 'J',
  12: 'Q',
  13: 'K',
  14: 'A',
};

const SUIT_LETTER: Record<SuitType, string> = {
  spades: 's',
  clubs: 'c',
  hearts: 'h',
  diamonds: 'd',
};

const ART_RANK_LETTER: Partial<Record<Rank, string>> = {
  11: 'j',
  12: 'q',
  13: 'k',
  14: 'a', // ace has its own illustrated centrepiece
};

/**
 * Pip positions for ranks 2..10. Coordinates are normalised to the card's
 * inner pip area (x=[0..1], y=[0..1]). Top/bottom rows use a tighter horizontal
 * span (0.32 / 0.68) so they don't visually collide with the corner rank+suit
 * indicators; only middle rows can use the full 0.25 / 0.75 span. Bottom-half
 * pips are rotated 180° per classical playing-card convention.
 */
const PIP_POS: Record<2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10, Array<[number, number]>> = {
  2: [
    [0.5, 0.05],
    [0.5, 0.95],
  ],
  3: [
    [0.5, 0.05],
    [0.5, 0.5],
    [0.5, 0.95],
  ],
  4: [
    [0.32, 0.05],
    [0.68, 0.05],
    [0.32, 0.95],
    [0.68, 0.95],
  ],
  5: [
    [0.32, 0.05],
    [0.68, 0.05],
    [0.5, 0.5],
    [0.32, 0.95],
    [0.68, 0.95],
  ],
  6: [
    [0.32, 0.05],
    [0.68, 0.05],
    [0.32, 0.5],
    [0.68, 0.5],
    [0.32, 0.95],
    [0.68, 0.95],
  ],
  7: [
    [0.32, 0.05],
    [0.68, 0.05],
    [0.5, 0.27],
    [0.32, 0.5],
    [0.68, 0.5],
    [0.32, 0.95],
    [0.68, 0.95],
  ],
  8: [
    [0.32, 0.05],
    [0.68, 0.05],
    [0.5, 0.27],
    [0.32, 0.5],
    [0.68, 0.5],
    [0.5, 0.73],
    [0.32, 0.95],
    [0.68, 0.95],
  ],
  9: [
    [0.32, 0.05],
    [0.68, 0.05],
    [0.32, 0.32],
    [0.68, 0.32],
    [0.5, 0.5],
    [0.32, 0.68],
    [0.68, 0.68],
    [0.32, 0.95],
    [0.68, 0.95],
  ],
  10: [
    [0.32, 0.05],
    [0.68, 0.05],
    [0.5, 0.2],
    [0.32, 0.35],
    [0.68, 0.35],
    [0.32, 0.65],
    [0.68, 0.65],
    [0.5, 0.8],
    [0.32, 0.95],
    [0.68, 0.95],
  ],
};

interface FaceProps {
  card: CardType;
  size?: 'sm' | 'md' | 'lg';
  selected?: boolean;
  onClick?: () => void;
}

interface SizeSpec {
  w: number;
  h: number;
  rank: number;
  cornerPip: number;
  centerPip: number;
  radius: number;
}

const SIZE: Record<'sm' | 'md' | 'lg', SizeSpec> = {
  sm: { w: 46, h: 66, rank: 10, cornerPip: 7, centerPip: 9, radius: 5 },
  md: { w: 64, h: 92, rank: 13, cornerPip: 9, centerPip: 12, radius: 7 },
  lg: { w: 88, h: 126, rank: 18, cornerPip: 12, centerPip: 17, radius: 9 },
};

function artImageUrl(card: CardType): string | null {
  const r = ART_RANK_LETTER[card.rank];
  if (!r) return null;
  return `/cards/face_${r}_${SUIT_LETTER[card.suit]}.png`;
}

export function CardFace({ card, size = 'md', selected, onClick }: FaceProps) {
  const dims = SIZE[size];
  const color = suitColor(card.suit);
  const artUrl = artImageUrl(card);
  const useArt = !!artUrl;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.94 }}
      animate={{
        y: selected ? -10 : 0,
        boxShadow: selected
          ? '0 0 0 2px var(--color-gold), 0 14px 22px -10px rgba(0,0,0,0.55)'
          : 'var(--shadow-card)',
      }}
      transition={{ type: 'spring', stiffness: 360, damping: 26 }}
      className="relative shrink-0 select-none overflow-hidden"
      style={{
        width: dims.w,
        height: dims.h,
        borderRadius: dims.radius,
        background: useArt
          ? `url(${artUrl}) center/cover no-repeat`
          : 'linear-gradient(180deg, #FBF6E7 0%, #F0E6CD 100%)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      {!useArt && <PipCardBody card={card} dims={dims} color={color} />}
    </motion.button>
  );
}

interface PipBodyProps {
  card: CardType;
  dims: SizeSpec;
  color: string;
}

function PipCardBody({ card, dims, color }: PipBodyProps) {
  const rank = card.rank as Exclude<Rank, 11 | 12 | 13 | 14>;
  const positions = PIP_POS[rank as keyof typeof PIP_POS] ?? [];
  const inset = Math.max(2, Math.round(dims.w * 0.05));
  const innerRadius = Math.max(2, dims.radius - 2);

  // Pip-area spans almost the full inner card; corner rank+suit indicators are small
  // enough (~12% of card height) that the top/bottom rows of pips at y=0.05/0.95
  // sit just below/above them. Top/bottom-row pips also use a tighter x-span
  // (0.32/0.68 in PIP_POS) so they never cross into the corner indicator boxes.
  const cornerH = Math.round(dims.h * 0.12);
  const pipAreaTop = cornerH;
  const pipAreaH = dims.h - cornerH * 2;

  return (
    <>
      {/* aged paper micro-grain */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.6) 0%, transparent 55%), radial-gradient(ellipse at 70% 90%, rgba(120, 90, 40, 0.08) 0%, transparent 60%)',
          mixBlendMode: 'overlay',
        }}
      />
      {/* gold double-line frame */}
      <div
        className="pointer-events-none absolute"
        style={{
          inset,
          border: '1px solid color-mix(in oklab, var(--color-gold) 65%, transparent)',
          borderRadius: innerRadius,
        }}
      />
      <div
        className="pointer-events-none absolute"
        style={{
          inset: inset + 2,
          border: '1px solid color-mix(in oklab, var(--color-gold) 35%, transparent)',
          borderRadius: Math.max(2, innerRadius - 2),
        }}
      />

      {/* corner: top-left rank + small pip */}
      <div
        className="absolute left-1.5 top-1 flex flex-col items-center leading-none"
        style={{ color }}
      >
        <span style={{ fontFamily: 'var(--font-display)', fontSize: dims.rank, fontWeight: 600 }}>
          {RANK_LABEL[card.rank]}
        </span>
        <Suit suit={card.suit} size={dims.cornerPip} />
      </div>
      {/* corner: bottom-right (rotated) */}
      <div
        className="absolute right-1.5 bottom-1 flex flex-col items-center leading-none rotate-180"
        style={{ color }}
      >
        <span style={{ fontFamily: 'var(--font-display)', fontSize: dims.rank, fontWeight: 600 }}>
          {RANK_LABEL[card.rank]}
        </span>
        <Suit suit={card.suit} size={dims.cornerPip} />
      </div>

      {/* central pips */}
      {positions.map(([x, y], i) => {
        const isBottomHalf = y > 0.5;
        const px = x * dims.w;
        const py = pipAreaTop + y * pipAreaH;
        return (
          <div
            key={i}
            className="absolute"
            style={{
              left: px,
              top: py,
              transform: `translate(-50%, -50%)${isBottomHalf ? ' rotate(180deg)' : ''}`,
            }}
          >
            <Suit suit={card.suit} size={dims.centerPip} />
          </div>
        );
      })}
    </>
  );
}

interface BackProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function CardBack({ size = 'md', className }: BackProps) {
  const dims = SIZE[size];
  return (
    <div
      className={`shrink-0 overflow-hidden ${className ?? ''}`}
      style={{
        width: dims.w,
        height: dims.h,
        borderRadius: dims.radius,
        background: 'url(/cards/back.png) center/cover no-repeat, #1F4F73',
        boxShadow: 'var(--shadow-card)',
      }}
    />
  );
}
